import MobileDrawer from '@c/drawers/MobileDrawer';
import BaseModal from '@c/modals/BaseModal';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import { deleteListing } from '@util/firestore/products';
import { isMobile } from '@util/index';
import { useToastContext } from 'context/ToastContext';
import { ProductDocument } from 'models/product';
import { useState } from 'react';

export default function DeleteListingModal({
  open,
  setOpen,
  product,
  onDelete,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  product: ProductDocument;
  onDelete: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastContext();

  const onDeleteListing = async () => {
    try {
      setLoading(true);
      await deleteListing(product?.id ?? '');

      queryClient.invalidateQueries({
        queryKey: ['listings'],
      });

      showSuccessToast('Listing deleted successfully');
      onDelete();
    } catch (e) {
      showErrorToast((e as Error).message);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const _isMobile = isMobile();

  const Base = _isMobile ? MobileDrawer : BaseModal;

  return (
    <Base
      show={open}
      isOpen={open}
      dismiss={() => setOpen(false)}
      title="Delete Listing"
    >
      <div className="flex h-[25rem] flex-col justify-between p-8">
        <span className="pt-6 text-body-lg font-medium text-text-muted">
          Are you sure you want to delete this listing? This action cannot be
          undone.
        </span>
        <div className="flex w-full justify-end gap-x-2">
          <Button
            text="Cancel"
            onClick={() => setOpen(false)}
            type="tertiary"
          />

          <Button
            text={loading ? 'Deleting...' : 'Delete'}
            onClick={onDeleteListing}
            type="secondary"
          />
        </div>
      </div>
    </Base>
  );
}
